import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { WHATSAPP_NUMBER, WA_DEFAULT_MESSAGE, formatWaDisplay, toIntlDigits } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PublicContentContext = createContext(null);

const defaultWaLink = (message) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message || WA_DEFAULT_MESSAGE)}`;

const empty = {
  testimonials: null,
  faqs: null,
  services_meta: null,
  contact: null,
  ai_concierge: null,
  hero: null,
  stats: null,
  why: null,
  about: null,
  sections: null,
  map: null,
  waLink: defaultWaLink,
  waDisplay: formatWaDisplay(WHATSAPP_NUMBER),
  phones: [{ display: formatWaDisplay(WHATSAPP_NUMBER), tel: `tel:+${WHATSAPP_NUMBER}` }],
};

export function PublicContentProvider({ children }) {
  const [content, setContent] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [tst, faq, svc, contact, ai, hero, stats, why, about, sections, map] = await Promise.all([
        axios.get(`${API}/list/testimonials`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/list/faqs`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/list/services_meta`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/content/contact`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/ai_concierge`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/homepage_hero`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/homepage_stats`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/homepage_why`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/about_page`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/homepage_sections`).then((r) => r.data.value).catch(() => null),
        axios.get(`${API}/content/homepage_map`).then((r) => r.data.value).catch(() => null),
      ]);
      setContent({ testimonials: tst, faqs: faq, services_meta: svc, contact, ai_concierge: ai, hero, stats, why, about, sections, map });
    } catch {
      setContent(empty);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const waNumber = toIntlDigits(content.contact?.whatsapp) || WHATSAPP_NUMBER;
  const waLink = useCallback(
    (message) => `https://wa.me/${waNumber}?text=${encodeURIComponent(message || WA_DEFAULT_MESSAGE)}`,
    [waNumber]
  );
  const waDisplay = formatWaDisplay(content.contact?.whatsapp || WHATSAPP_NUMBER);
  const phones = [content.contact?.phone || waNumber, content.contact?.phone2]
    .filter((p) => toIntlDigits(p))
    .map((p) => ({ display: formatWaDisplay(p), tel: `tel:+${toIntlDigits(p)}` }));

  return (
    <PublicContentContext.Provider value={{ ...content, reload: load, waLink, waDisplay, phones }}>
      {children}
    </PublicContentContext.Provider>
  );
}

export const usePublicContent = () => useContext(PublicContentContext) || empty;
